import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { ApplicationState } from "../../store"
import { connect, useDispatch } from "react-redux"
import { apiV2, wrapResponse } from "../../repositories/repository"
import { CompactTable } from "@table-library/react-table-library/compact"
import { useTheme } from "@table-library/react-table-library/theme"
import { getTheme } from "@table-library/react-table-library/mantine"
import Paginator from "../../components/Paginator/Paginator"
import Toggleable from "../../rc-components/Toggleable/Toggleable"
import SelectorButton from "../../components/SelectorButton/SelectorButton"
import { ReactComponent as FilterIcon } from "../../assets/img/icons/filter-icon.svg"
import useTranslate from "../../hooks/useTranslate"
import { openModalWindow } from "../../store/ModalWindowData/action"
import dayjs from "dayjs"
import useSWR from "swr"
import { AppLanguage } from "../../sdk"

const Subscribers: React.FC<any> = () => {
  const theme = useTheme([
    getTheme(),
    {
      Table: `
        --data-table-library_grid-template-columns:
  minmax(0, 2fr)
  minmax(0, 1fr)
  minmax(0, 2fr)
  minmax(0, 1fr)
  minmax(0, 1fr)
  minmax(0, 1fr)
   minmax(0, 1fr)
   minmax(0, 0.5fr) !important;
      `,
    },
  ])

  type CallMeItem = {
    phone: string
    customSlot?: string
    selectedDays?: string[]
    createdAt: string
  }
  type SubscriberItem = {
    _id: string
    userId: string | null
    pnr: string | null
    name: string
    email: string
    phone: string | null
    riskScore: number | null
    pointsByGroup?: any
    cognitiveIssues: boolean | null
    createdAt: string
    questionnaire?: { slug: string; type: string; answers: string[] }[]
    whatWouldWorkBetter?: string
    additionalInfo?: string
    subscribe?: boolean
    hasBooked?: boolean
    wantsToBeContacted?: boolean
    customSlot?: string
    selectedDays?: string[]
    triggeredFingers?: { social: boolean, cardio: boolean, physical: boolean, food: boolean, mental: boolean }
  }

  type SubscriberListResponse = {
    items: SubscriberItem[]
    total: number
    page: number
    limit: number
    totalPages: number
  }

  type WantsToBeContactedAnswer = 'short-questionnaire.yes' | 'short-questionnaire.no'

  const [items, setItems] = useState<SubscriberItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // filters
  const [q, setQ] = useState('')
  const [remindInYear, setRemindInYear] = useState<boolean | ''>('')
  const [cognitiveIssues, setCognitiveIssues] = useState<boolean | ''>('')
  const [minRiskScore, setMinRiskScore] = useState<number | ''>('')
  const [maxRiskScore, setMaxRiskScore] = useState<number | ''>('')
  const [municipality, setMunicipality] = useState<'all' | 'geras' | 'gustafkliniken' | 'capio'>('all')
  const [sortBy, setSortBy] = useState<'createdAt' | 'riskScore' | 'name' | 'email'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [language, setLanguage] = useState<AppLanguage | ''>('')
  const [wantsToBeContacted, setWantsToBeContacted] = useState<WantsToBeContactedAnswer | ''>('')
  const [reloadKey, setReloadKey] = useState(0)
  const translateLocalazy = useTranslate()
  const dispatch = useDispatch()

  const queryParams = useMemo(() => ({
    q: q || undefined,
    cognitiveIssues: cognitiveIssues === '' ? undefined : cognitiveIssues,
    remindInYear: remindInYear === '' ? undefined : remindInYear,
    minRiskScore: minRiskScore === '' ? undefined : Number(minRiskScore),
    maxRiskScore: maxRiskScore === '' ? undefined : Number(maxRiskScore),
    wantsToBeContacted: wantsToBeContacted || undefined,
    page,
    limit,
    sortBy,
    sortOrder,
    language,
    municipality
  }), [q, cognitiveIssues, minRiskScore, maxRiskScore, wantsToBeContacted, page, limit, sortBy, sortOrder, language, remindInYear, municipality])

  const {
    data: calls,
  } = useSWR(
    'api.subscriber.getCalls',
    () => apiV2.subscriber.getCalls(),
    {revalidateOnFocus: false}
  )

  const fetchSubscribers = useCallback(async () => {
    setLoading(true)
    setError(null)
    const res = await wrapResponse<SubscriberListResponse>(apiV2.request.request({
      method: 'GET',
      url: '/subscriber/list',
      query: queryParams,
    }))
    if (res.success) {
      setItems(res.data.items)
      setTotal(res.data.total)
    } else {
      setError(res.error)
    }
    setLoading(false)
  }, [queryParams])

  useEffect(() => {
    fetchSubscribers()
  }, [page, limit, reloadKey, fetchSubscribers])

  const onSearch = () => {
    setPage(1)
    setReloadKey((k) => k + 1)
  }

  const onClear = () => {
    setQ('')
    setCognitiveIssues('')
    setMunicipality('all')
    setRemindInYear('')
    setMinRiskScore('')
    setMaxRiskScore('')
    setSortBy('createdAt')
    setSortOrder('desc')
    setLanguage('')
    setWantsToBeContacted('')
    setPage(1)
    setLimit(20)
    setReloadKey((k) => k + 1)
  }

  const callsColumns = [
    {label: 'Phone', renderCell: (item: CallMeItem) => item.phone.replaceAll(' ', '')},
    {label: 'Desired time', renderCell: (item: CallMeItem) => item.customSlot || '-'},
    {label: 'Desired days', renderCell: (item: CallMeItem) => item.selectedDays?.join(', ') || '-'},
    {
      label: 'Date', renderCell: (item: SubscriberItem) => {
        const d = dayjs(item.createdAt)
        return <>
          {d.format('DD MMM')}
          <br/>
          {d.format('HH:mm')}
        </>
      },
    },
  ]
  const columns = [
    {
      label: 'Contact data',
      renderCell: (item: SubscriberItem) => (<>
        {item._id}
        {item.name}
        <br/>
        {item.email ? (
          <a
            href={`mailto:${item.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {item.email}
          </a>
        ) : '-'}
        <br/>
        {item.phone || ''}
      </>)
    },
    {
      label: 'PNR',
      renderCell: (item: SubscriberItem) => (item.pnr ?? '-')
    },
    {
      label: translateLocalazy('subscribers.risk-score'),
      renderCell: (item: SubscriberItem) => {
        const risk = [<span key={0}>{(item.riskScore ?? '-') + ((item.riskScore ?? 0) > 8 ? ' (risk)' : '')}</span>]
        if (item.pointsByGroup) {
          risk.push(<br/>)
          for (const groupName in item.pointsByGroup) {
            const amountOfPoints = item.pointsByGroup[groupName].reduce((a, b) => a + b.points, 0)
            risk.push(<span>{groupName} - {amountOfPoints}</span>)
            risk.push(<br/>)
          }
        }

        return risk
      }
    },

    {
      label: 'Fingers',
      renderCell: (item: SubscriberItem) => {
        if (item.triggeredFingers) {
          const text: ReactElement[] = []
          for (const key in item.triggeredFingers) {
            if (item.triggeredFingers[key]) {
              text.push(<span>{key}</span>)
              text.push(<br/>)
            }
          }

          return text
        }

        return '-'
      }
    },
    {
      label: translateLocalazy('subscribers.cognitive-issues'),
      renderCell: (item: SubscriberItem) => item.cognitiveIssues ? 'Yes' : 'No'
    },
    {
      label: translateLocalazy('subscribers.relative'), renderCell: (item: SubscriberItem) => {
        const q = item.questionnaire || []
        const isPatient = q.find(e => e?.slug === 'who-is-answering')?.answers.includes('patient')
        return isPatient ? 'No' : 'Yes'
      }
    },
    {
      label: 'Contact me', renderCell: (item: SubscriberItem) => {
        const q = item.questionnaire || []
        const wantsQuestion = q.find(e => e?.slug === 'wants-to-be-contacted')
        const wants = wantsQuestion?.answers.includes('short-questionnaire.yes') || item.wantsToBeContacted
        const answer = wants ? 'Yes' : 'No'
        if (item.wantsToBeContacted) {
          return <>
            {answer}
            <br/>
            Day: {(item.selectedDays ?? []).join(',') || 'any'}
            <br/>
            Time: {item.customSlot ?? 'any'}
          </>
        }
        return answer
      }
    },
    {
      label: translateLocalazy('subscribers.date'),
      renderCell: (item: SubscriberItem) => {
        const d = dayjs(item.createdAt)
        return <>
          {d.format('DD MMM')}
          <br/>
          {d.format('HH:mm')}
        </>
      }
    },
  ]

  const rowProps = {
    onClick: (item: SubscriberItem) => {
      const entries = (item.questionnaire || []).map((q) => {
        const skipAnswerTranslation = ['influence-circumstances'].includes(q.slug)
        return {
          question: translateLocalazy(q.slug),
          answer: q.type === 'input' || skipAnswerTranslation
            ? (q.answers || []).join(', ')
            : (q.answers || []).map((a) => translateLocalazy(a)).join(', ')
        }
      })
      dispatch(openModalWindow('subscriberQuestionnaire' as any, {
        entries,
        name: item.name,
        email: item.email,
        additionalInfo: item.additionalInfo,
        customSlot: item.customSlot,
        selectedDays: item.selectedDays,
        pointsByGroup: item.pointsByGroup
      } as any))
    },
    style: {cursor: 'pointer'}
  }

  return (<>
    <div className="card-box patient-info">
      <div className="box-title">
        <h3>Calls</h3>
      </div>
      <CompactTable columns={callsColumns} data={{nodes: calls || []}} theme={getTheme()}/>
    </div>

    <div className="card-box patient-info">
      <div className="box-title">
        <h3>Subscribers</h3>
      </div>

      <div className="search-box">
        <div className="flex-container justify-between align-start">
          <div className="search_filters-row flex-item item-sm-12">
            <Toggleable>
              {(dropdownId, setDropdownId) => (
                <div className="flex-container">
                  <div className="flex-item flex-item--shrink search_filters-box">
                    <SelectorButton
                      Elem={
                        <div className="search_filters-block">
                          <div className="control-holder">
                            <label>{translateLocalazy('search')}</label>
                            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                                   placeholder={translateLocalazy('Type here...')}/>
                          </div>

                          <div className="control-holder">
                            <label>{translateLocalazy('subscribers.municipality')}</label>
                            <select value={municipality}
                                    onChange={(e) => {
                                      // @ts-ignore
                                      setMunicipality(e.target.value)
                                    }}>
                              <option value="all">{translateLocalazy('subscribers.municipality.all')}</option>
                              <option value="geras">{translateLocalazy('subscribers.municipality.geras')}</option>
                              <option
                                value="gustafkliniken">{translateLocalazy('subscribers.municipality.gustafkliniken')}</option>
                              <option value="capio">{translateLocalazy('subscribers.municipality.capio')}</option>
                            </select>
                          </div>
                          <div className="control-holder">
                            <label>{translateLocalazy('subscribers.cognitive-issues')}</label>
                            <select value={cognitiveIssues === '' ? '' : cognitiveIssues ? 'true' : 'false'}
                                    onChange={(e) => setCognitiveIssues(e.target.value === '' ? '' : e.target.value === 'true')}>
                              <option value="">{translateLocalazy('all')}</option>
                              <option value="true">{translateLocalazy('yes')}</option>
                              <option value="false">{translateLocalazy('no')}</option>
                            </select>
                          </div>
                          <div className="control-holder">
                            <label>Remind in year</label>
                            <select value={remindInYear === '' ? '' : remindInYear ? 'true' : 'false'}
                                    onChange={(e) => setRemindInYear(e.target.value === '' ? '' : e.target.value === 'true')}>
                              <option value="">{translateLocalazy('all')}</option>
                              <option value="true">{translateLocalazy('yes')}</option>
                              <option value="false">{translateLocalazy('no')}</option>
                            </select>
                          </div>

                          <div className="control-holder">
                            <label>{translateLocalazy('subscribers.wants-to-be-contacted')}</label>
                            <select
                              value={wantsToBeContacted}
                              onChange={(e) => {
                                const value = e.target.value as WantsToBeContactedAnswer | ''
                                setWantsToBeContacted(value)
                              }}
                            >
                              <option value="">{translateLocalazy('all')}</option>
                              <option value="short-questionnaire.yes">{translateLocalazy('yes')}</option>
                              <option value="short-questionnaire.no">{translateLocalazy('no')}</option>
                            </select>
                          </div>
                          <div className="control-holder">
                            <label>Language</label>
                            <select
                              value={language}
                              onChange={(e) => {
                                setLanguage(e.target.value as AppLanguage)
                              }}
                            >
                              <option value="">All</option>
                              <option value="sv">Swedish</option>
                              <option value="en">English</option>
                              <option value="da">Danish</option>
                            </select>
                          </div>

                          <div className="control-holder">
                            <label>{translateLocalazy('subscribers.risk-score')}</label>
                            <div className="flex-container">
                              <div className="flex-item item-sm-6">
                                <label>min</label>
                                <input type="number" value={minRiskScore}
                                       onChange={(e) => setMinRiskScore(e.target.value === '' ? '' : Number(e.target.value))}/>
                              </div>
                              <div className="flex-item item-sm-6">
                                <label>max</label>
                                <input type="number" value={maxRiskScore}
                                       onChange={(e) => setMaxRiskScore(e.target.value === '' ? '' : Number(e.target.value))}/>
                              </div>
                            </div>
                          </div>

                          <div className="control-holder">
                            <label>{translateLocalazy('sort')}</label>
                            <div className="flex-container">
                              <div className="flex-item item-sm-6">
                                <label>{translateLocalazy('sort-by')}</label>
                                <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
                                  <option value="createdAt">{translateLocalazy('subscribers.date')}</option>
                                  <option value="riskScore">{translateLocalazy('subscribers.risk-score')}</option>
                                  <option value="name">{translateLocalazy('subscribers.name')}</option>
                                  <option value="email">{translateLocalazy('subscribers.email')}</option>
                                </select>
                              </div>
                              <div className="flex-item item-sm-6">
                                <label>{translateLocalazy('order')}</label>
                                <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)}>
                                  <option value="desc">{translateLocalazy('desc')}</option>
                                  <option value="asc">{translateLocalazy('asc')}</option>
                                </select>
                              </div>
                            </div>
                          </div>

                          <div className="control-holder">
                            <label>{translateLocalazy('limit')}</label>
                            <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </select>
                          </div>

                          <div className="btn-holder flex justify-end">
                            <button onClick={() => {
                              onClear()
                              setDropdownId('')
                            }} className="g_btn g_btn--outlined t_uppercase"
                                    type="button">{translateLocalazy('clear')}</button>
                            <button onClick={() => {
                              onSearch()
                              setDropdownId('')
                            }} className="g_btn g_btn--primary t_uppercase"
                                    type="button">{translateLocalazy('search')}</button>
                          </div>
                        </div>
                      }
                      icon={<FilterIcon className="search_filter-icon"/>}
                      classPrefix={'--filters'}
                      type={'subscriberFilters'}
                      title={'filters'}
                      dropdownId={dropdownId}
                      onToggleMenu={setDropdownId}
                    />
                  </div>
                </div>
              )}
            </Toggleable>
          </div>
        </div>
      </div>

      <div>
        {loading && <div className="flex" style={{padding: '16px'}}>{translateLocalazy('Loading...')}</div>}
        {error && <div className="login_form-error" style={{padding: '16px'}}>{error}</div>}
        {!loading && items.length === 0 && !error && (
          <div className="card-box no-data flex flex-column">
            <div className="no-data-text">{translateLocalazy('noData')}</div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="card-box">
            <CompactTable columns={columns} data={{nodes: items}} theme={theme} rowProps={rowProps}/>
            <br/>
            <Paginator
              totalItemsCount={total}
              pageSize={limit}
              currentPage={page}
              onChangePage={setPage}
            />
          </div>
        )}
      </div>
    </div>
  </>)
}

const mapStateToProps = (state: ApplicationState): any => ({
  stats: state.voucherCodes.stats,
  user: state.auth.user,
})

const mapDispatchToProps = (): any => ({})

export default connect(mapStateToProps, mapDispatchToProps)(Subscribers)
